import {alias, createModelSchema, custom, deserialize, list, serialize, SKIP} from "serializr";
import {Cloneable} from "@/common/Cloneable";

import {Currency} from "../../currency/Currency";
import {ItemUpgradeRule} from "@/modules/redactor/models/item/upgrade/ItemUpgradeRule";
import {ItemUpgradeMode} from "@/modules/redactor/models/item/upgrade/ItemUpgradeMode";
import {ItemKey} from "@/modules/redactor/models/item/core/ItemKey";
import {RedactorUtils} from "@/modules/redactor/core/RedactorUtils";
import {Schema} from "@/common/Schema";

export class ItemUpgradeConfig extends Cloneable {

    static schema() {
        return custom(
            (v) => {
                return v && !v.empty() ? serialize(ItemUpgradeConfig, v) : SKIP;
            },
            (v) => {
                return deserialize(ItemUpgradeConfig, v);
            }
        );
    }

    constructor() {
        super();

        this._mode = ItemUpgradeMode.DISABLED;

        this._rules = [];
        this._price = [];
        this._duration = 0;
        this._upgradeItem = null;

        this._adPlacement = null;

        this._questAllow = null;
    }

    get mode() {
        return this._mode;
    }

    set mode(value) {
        this._mode = value;
        this._rules = [];
        this._price = [];
    }

    get rules() {
        return this._rules;
    }

    set rules(value) {
        this._rules = value;
    }

    get price() {
        return this._price;
    }

    set price(value) {
        this._price = value;
    }

    get duration() {
        return this._duration;
    }

    set duration(value) {
        this._duration = value;
    }

    get upgradeItem() {
        return this._upgradeItem;
    }

    set upgradeItem(value) {
        this._upgradeItem = value;
    }

    get adPlacement() {
        return this._adPlacement;
    }

    set adPlacement(value) {
        this._adPlacement = value;
    }

    get questAllow() {
        return this._questAllow;
    }

    set questAllow(value) {
        this._questAllow = value;
    }

    valid() {

        if (this.mode.equal(ItemUpgradeMode.DISABLED)) {
            return true;
        } else if (this.mode.equal(ItemUpgradeMode.ITEM_AMOUNT)) {
            if (!this.rules || this.rules.length === 0) {
                return false;
            }
        } else if (this.mode.equal(ItemUpgradeMode.ITEM_POINTS)) {
            if (!this.rules || this.rules.length === 0) {
                return false;
            }
        } else if (this.mode.equal(ItemUpgradeMode.PRICE)) {
            if (!this.price || this.price.length === 0) {
                return false;
            }
        } else {
            return false;
        }

        if (!Number.isInteger(this.duration) || this.duration < 0) {
            return false;
        }

        if (this.price && this.price.some(v => !v || !v.valid())) {
            return false;
        }

        if (this.rules) {

            if (this.rules.some(v => !v || !v.valid())) {
                return false;
            }

            for (let i = 0; i < this.rules.length - 1; i++) {
                for (let j = i + 1; j < this.rules.length; j++) {
                    if (this.rules[i].collide(this.rules[j])) {
                        return false;
                    }
                }
            }
        }

        if (this.upgradeItem && (!this.upgradeItem.valid() || this.upgradeItem.any())) {
            return false;
        }

        if (this.adPlacement && !RedactorUtils.getAdPlacement(this.adPlacement)) {
            return false;
        }

        if (this.questAllow && !RedactorUtils.getQuest(this.questAllow)) {
            return false;
        }

        return true;
    }

    empty() {
        return this.mode.equal(ItemUpgradeMode.DISABLED);
    }

    clone(target = undefined) {
        if (!target) {
            return super.clone(new ItemUpgradeConfig());
        } else if (target instanceof ItemUpgradeConfig) {
            return super.clone(target);
        } else {
            console.assert(false);
            return undefined;
        }
    }
}

createModelSchema(ItemUpgradeConfig, {
    _mode: alias("mode", ItemUpgradeMode.schema()),
    _rules: alias("rules", list(ItemUpgradeRule.schema())),
    _price: alias("price", list(Currency.schema())),
    _duration: alias("duration", Schema.primitive()),
    _upgradeItem: alias("upgrade_item", ItemKey.schema()),
    _adPlacement: alias("ad_placement", Schema.primitive()),
    _questAllow: alias("quest_allow", Schema.primitive()),
});